// @ts-nocheck

import React, { ReactNode, useContext, useState, useCallback, Children, useEffect, ChangeEvent } from "react";
import cn from "classnames";

import globalFormStyles from "../global.formElements.module.scss";
import FieldMessage from "../FieldMessage";
import { CheckboxNestedGroupProps } from "./CheckboxNestedGroup.props";
import LabelTextItem from "../../LabelTextItem";
import Label from "../Label";
import Checkbox from "../Checkbox";

import { FormContext, useFormField } from "../../Form";

/**
 * @deprecated Use `import { CheckboxGroup } from capitalrx-components-2.0/lib/forms` instead.
 */
const CheckboxGroup: React.FC<CheckboxNestedGroupProps> = (props: CheckboxNestedGroupProps) => {
  const {
    children,
    name,
    id,
    label,
    validate,
    helperText,
    disabled,
    displayReadOnlyFieldInline,
    large,
    containerClassName,
    onChange,
    readOnly,
    useAsterisk,
    isLoading,
  } = props;

  const { formState, readOnly: readOnlyForm } = useContext(FormContext);
  const { errors, isFormLoading } = formState;

  const fieldValue = useFormField(name);

  const childCountArray = Children.toArray(children).length;
  const [childCheckboxState, setChildCheckboxState] = useState<boolean>(false);
  const [parentCheckboxState, setParentCheckboxState] = useState<boolean>(false);
  const [indeterminate, setIndeterminate] = useState<boolean>(false);

  const checkboxContainerClass = cn(globalFormStyles["form-field-container"], containerClassName);
  const displayLabel = label && <Label id={`label-${id}`} label={label} validate={validate} useAsterisk={useAsterisk} />;

  const readyOnlyStyles = cn({ [globalFormStyles["selection-field-read-only"]]: !displayReadOnlyFieldInline });

  const getCheckedCount = () => {
    const checkedCheckBox = document.querySelector(`#checkbox-group-list-${id}`);
    return checkedCheckBox? checkedCheckBox.querySelectorAll(`input[name^='${name}']:checked:not(#${id}-nested-parent-checkbox)`).length: 0;
  };

  const setParentCheckbox = (value: string[] | number): void => {
    let childValue = value ? value : 0;

    if (Array.isArray(value)) {
      childValue = value.length;
    }

    if (childValue === 0) {
      setIndeterminate(false);
      setParentCheckboxState(false);
      setChildCheckboxState(false);
      // @ts-ignore
    } else if (childValue < childCountArray) {
      setIndeterminate(true);
    } else if (childValue === childCountArray) {
      setParentCheckboxState(true);
      setChildCheckboxState(true);
      setIndeterminate(false);
    }
  };

  useEffect(() => {
    // @ts-ignore
    setParentCheckbox(fieldValue);
  }, []);

  const renderChildrenWithProps = (child: JSX.Element): ReactNode => {
    const { props } = child;

    if (props) {
      return (
        <Checkbox
          id={`${id}-${props.value.split(" ").join("-").toLowerCase()}`}
          name={name}
          large={large}
          isLoading={isLoading || isFormLoading}
          disabled={disabled ? disabled : props.disabled}
          label={props.label}
          value={props.value}
          validate={validate}
          onChange={(e) => {
            setParentCheckbox(getCheckedCount());
            props.onChange && props.onChange(e);
          }}
          isChecked={childCheckboxState}
        />
      );
    }
  };

  const handleParentCheckboxChange = useCallback(
    (ev: ChangeEvent<HTMLInputElement>): void => {
      setIndeterminate(false);
      setParentCheckboxState((PrevState) => !PrevState);
      setChildCheckboxState((PrevState) => !PrevState);
      onChange && onChange(ev);
    },
    [childCheckboxState],
  );

  let component = null;
  if (readOnly || readOnlyForm) {
    const checkboxValue = fieldValue && new Array(String(fieldValue)).filter(Boolean).join("\r\n");
    component = (
      <LabelTextItem
        id={id}
        label={label}
        displayInline={displayReadOnlyFieldInline}
        body={checkboxValue}
        isLoading={isLoading || isFormLoading}
        className={readyOnlyStyles}
      />
    );
  } else {
    component = (
      <>
        {displayLabel}
        <Checkbox
          name={name}
          id={id ? `${id}-nested-parent-checkbox` : undefined}
          label={label}
          large={large}
          disabled={disabled}
          containerClassName="nested-parent-checkbox"
          onChange={handleParentCheckboxChange}
          isChecked={parentCheckboxState}
          indeterminate={indeterminate}
          isLoading={isLoading || isFormLoading}
        />
        <div id={`checkbox-group-list-${id}`} className={globalFormStyles["selection-field-children-container"]} onChange={onChange}>
          {React.Children.map(children, (child: ReactNode) => {
            return renderChildrenWithProps(child as JSX.Element);
          })}
        </div>
        <FieldMessage id={id} helperText={helperText} errors={errors} name={name} />
      </>
    );
  }

  return <div className={checkboxContainerClass}>{component}</div>;
};

/**
 * @deprecated
 */
export default CheckboxGroup;
